import { z } from 'zod';
import { HouseholdId, IsoDatetime, MemoryEntryId, UserId } from './primitives.js';

export const MemoryCategory = z.enum([
  'cleaning_standards',
  'shopping_preferences',
  'car_rules',
  'guest_handling',
  'deliveries',
  'emergency_procedures',
  'contacts',
  'other',
]);
export type MemoryCategory = z.infer<typeof MemoryCategory>;

// Who a rule governs (migration 0051): the whole household (default, and
// every rule written before scopes existed), one worker (narrowed via
// visible_to_user_ids), or anyone doing that kind of work (craft knowledge).
export const MemoryScope = z.enum(['household', 'worker', 'general_practice']);
export type MemoryScope = z.infer<typeof MemoryScope>;

// Where a rule came from: written/confirmed by the owner, or observed from
// household patterns by the intelligence layer.
export const MemoryProvenance = z.enum(['owner_confirmed', 'observed']);
export type MemoryProvenance = z.infer<typeof MemoryProvenance>;

export const MemoryEntry = z.object({
  id: MemoryEntryId,
  household_id: HouseholdId,
  category: MemoryCategory,
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(8000),
  tags: z.array(z.string().max(40)).max(20),
  pinned: z.boolean(),
  visible_to_user_ids: z.array(UserId),
  phone: z.string().nullable(),
  source_user_id: UserId.nullable(),
  scope: MemoryScope,
  provenance: MemoryProvenance,
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
  expires_at: IsoDatetime.nullable(),
});
export type MemoryEntry = z.infer<typeof MemoryEntry>;

export const CreateMemoryEntryRequest = z.object({
  category: MemoryCategory,
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(8000),
  tags: z.array(z.string().max(40)).max(20).default([]),
  pinned: z.boolean().default(false),
  // Empty = everyone. Naming people narrows it to them (the owner always
  // sees everything). Default open on purpose: a rule nobody can read
  // protects nothing, so forgetting this field shares rather than hides.
  visible_to_user_ids: z.array(UserId).max(20).default([]),
  // Default household-wide, which is what every rule before scopes was. A
  // 'worker' rule only governs the people named in visible_to_user_ids.
  scope: MemoryScope.default('household'),
  // The number to tap. Not validated as E.164 — a household writes
  // "04-123 4567" and "+971 50 …" and both should dial.
  phone: z.string().max(40).optional(),
  // Null clears an existing expiry; omitted leaves it untouched on update.
  // Most rules never set this.
  expires_at: IsoDatetime.nullable().optional(),
});
export type CreateMemoryEntryRequest = z.infer<typeof CreateMemoryEntryRequest>;

export const UpdateMemoryEntryRequest = CreateMemoryEntryRequest.partial();
